import type { Entries } from './catalog';

/**
 * The copy of invitations sent and received, mail preferences and security activity (E2, ACC-12,
 * ACC-16, ACC-24, ARR-03, ARR-06, ARR-23).
 *
 * Each area keeps its own file so its keys travel with its screens; a key here must not repeat one of
 * `en.ts`, `es.ts` or another area.
 */
export const invites: Record<'en' | 'es', Entries> = {
	en: {
		'invitations.emails': 'Email addresses',
		'invitations.emails.help': 'One or more, separated by commas or new lines. Each person signs in with that address to accept.',
		'invitations.emails.many': 'Invite up to {max} addresses at once.',
		'invitations.role.help': 'What they can do',
		'invitations.results': 'Invitations sent',
		'invitations.delivery.sent': 'Sent to {email}.',
		'invitations.delivery.failed': 'The message to {email} was not sent. Copy the link and send it yourself.',
		'invitations.delivery.off': 'Copy the link and send it to {email}.',
		'invitations.delivery.error': '{email}: {reason}',
		'invitations.resend': 'Send again',
		'invitations.resent': 'Sent again with a new link. The previous link no longer works.',
		'invitations.show.open': 'Open invitations',
		'invitations.show.all': 'Accepted and withdrawn too',
		'invitations.erased': 'Deleted account',
		'invitations.by': 'Invited by {name}',
		'invitations.actions': 'Actions for the invitation to {email}',
		'invitations.alreadyMember': 'That address already belongs to a member.',

		'received.title': 'Invitations',
		'received.palette': 'Your invitations',
		'received.none': 'No invitations waiting.',
		'received.item': '{inviter} invited you to {organization}',
		'received.someone': 'Someone',
		'received.as': 'As {role} · expires {date}',
		'received.from': 'From {product}',
		'received.accept': 'Accept',
		'received.decline': 'Decline',
		'received.joined': 'You joined {organization}.',
		'received.open': 'Open {product}',
		'received.go': 'Go to {organization}',
		'received.declined': 'Invitation declined.',

		'invitation.by': '{inviter} invited you as {role}.',
		'invitation.from': 'They sent it from {product}.',
		'invitation.joined': 'You joined {organization}.',

		'activity.none': 'Nothing yet.',
		'activity.more': 'Show older',
		'activity.signin': 'Signed in',
		'activity.signup': 'Created the account',
		'activity.account.erase': 'Deleted the account',
		'activity.account.delete': 'Asked to delete the account',
		'activity.account.restore': 'Kept the account',
		'activity.security.signin.new': 'Signed in from a new device',
		'activity.security.signin.throttled': 'Several failed sign-in attempts',
		'activity.security.password.changed': 'Changed the password',
		'activity.security.password.reset': 'Reset the password',
		'activity.security.email.changed': 'Changed the email',
		'activity.security.identity.attached': 'Added a way to sign in',
		'activity.security.identity.detached': 'Removed a way to sign in',
		'activity.security.factor.added': 'Added a second factor',
		'activity.security.factor.removed': 'Removed a second factor',
		'activity.security.factor.recovery': 'Used a recovery code',
		'activity.security.sessions.ended': 'Signed out of other sessions',
		'activity.security.account.deletion': 'Scheduled the account for deletion',
		'activity.security.account.restored': 'Cancelled the account’s deletion',
		'activity.security.token.created': 'Created a personal access token',
		'activity.security.connection.created': 'Gave an AI app access',
		'activity.security.connection.ended': 'Removed an AI app’s access',
		'activity.security.connection.narrowed': 'Removed some of an AI app’s permissions',
		'activity.security.access.ended': 'Removed the access of AI apps and access tokens',
		'activity.refused': 'Refused',
		'activity.ended': { one: 'One other session was signed out', other: '{count} other sessions were signed out' },

		'mail.title': 'Email',
		'mail.account': 'Messages about your account, its security and your invitations are always sent.',
		'mail.work': 'Work notices from the products',
		'mail.work.off': 'Don’t email me',
		'mail.work.immediate': 'Email each one',
		'mail.work.digest': 'One email a day',
		'mail.saved': 'Email preference saved.'
	},
	es: {
		'invitations.emails': 'Direcciones de correo',
		'invitations.emails.help': 'Una o varias, separadas por comas o líneas. Cada persona inicia sesión con esa dirección para aceptar.',
		'invitations.emails.many': 'Invita hasta {max} direcciones a la vez.',
		'invitations.role.help': 'Lo que puede hacer',
		'invitations.results': 'Invitaciones enviadas',
		'invitations.delivery.sent': 'Enviada a {email}.',
		'invitations.delivery.failed': 'El mensaje a {email} no se envió. Copia el enlace y envíalo tú.',
		'invitations.delivery.off': 'Copia el enlace y envíaselo a {email}.',
		'invitations.delivery.error': '{email}: {reason}',
		'invitations.resend': 'Enviar otra vez',
		'invitations.resent': 'Se envió otra vez con un enlace nuevo. El enlace anterior ya no funciona.',
		'invitations.show.open': 'Invitaciones abiertas',
		'invitations.show.all': 'También aceptadas y retiradas',
		'invitations.erased': 'Cuenta eliminada',
		'invitations.by': 'Invitó {name}',
		'invitations.actions': 'Acciones para la invitación a {email}',
		'invitations.alreadyMember': 'Esa dirección ya pertenece a un miembro.',

		'received.title': 'Invitaciones',
		'received.palette': 'Tus invitaciones',
		'received.none': 'No hay invitaciones esperando.',
		'received.item': '{inviter} te invitó a {organization}',
		'received.someone': 'Alguien',
		'received.as': 'Como {role} · vence el {date}',
		'received.from': 'Desde {product}',
		'received.accept': 'Aceptar',
		'received.decline': 'Rechazar',
		'received.joined': 'Te uniste a {organization}.',
		'received.open': 'Abrir {product}',
		'received.go': 'Ir a {organization}',
		'received.declined': 'Invitación rechazada.',

		'invitation.by': '{inviter} te invitó como {role}.',
		'invitation.from': 'La envió desde {product}.',
		'invitation.joined': 'Te uniste a {organization}.',

		'activity.none': 'Nada todavía.',
		'activity.more': 'Mostrar anteriores',
		'activity.signin': 'Inició sesión',
		'activity.signup': 'Creó la cuenta',
		'activity.account.erase': 'Eliminó la cuenta',
		'activity.account.delete': 'Pidió eliminar la cuenta',
		'activity.account.restore': 'Conservó la cuenta',
		'activity.security.signin.new': 'Inició sesión desde un dispositivo nuevo',
		'activity.security.signin.throttled': 'Varios intentos fallidos de iniciar sesión',
		'activity.security.password.changed': 'Cambió la contraseña',
		'activity.security.password.reset': 'Restableció la contraseña',
		'activity.security.email.changed': 'Cambió el correo',
		'activity.security.identity.attached': 'Añadió un método de acceso',
		'activity.security.identity.detached': 'Quitó un método de acceso',
		'activity.security.factor.added': 'Añadió un segundo factor',
		'activity.security.factor.removed': 'Quitó un segundo factor',
		'activity.security.factor.recovery': 'Usó un código de recuperación',
		'activity.security.sessions.ended': 'Cerró las otras sesiones',
		'activity.security.account.deletion': 'Programó la eliminación de la cuenta',
		'activity.security.account.restored': 'Canceló la eliminación de la cuenta',
		'activity.security.token.created': 'Creó un token de acceso personal',
		'activity.security.connection.created': 'Dio acceso a una app de IA',
		'activity.security.connection.ended': 'Quitó el acceso de una app de IA',
		'activity.security.connection.narrowed': 'Quitó algunos permisos de una app de IA',
		'activity.security.access.ended': 'Quitó el acceso de las apps de IA y los tokens de acceso',
		'activity.refused': 'Rechazado',
		'activity.ended': { one: 'Se cerró otra sesión', other: 'Se cerraron otras {count} sesiones' },

		'mail.title': 'Correo',
		'mail.account': 'Los mensajes sobre tu cuenta, su seguridad y tus invitaciones siempre se envían.',
		'mail.work': 'Avisos de trabajo de los productos',
		'mail.work.off': 'No enviarme correo',
		'mail.work.immediate': 'Un correo por cada uno',
		'mail.work.digest': 'Un correo al día',
		'mail.saved': 'Preferencia de correo guardada.'
	}
};
